import React from 'react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';
import { Title, UniversalLink } from '@plone/volto/components';
import { Link } from '@fhnw/components/Link/Link';
import SlotRenderer from '@plone/volto/components/theme/SlotRenderer/SlotRenderer';
import type { Content as PloneContent } from '@plone/types';
import { Helmet } from '@plone/volto/helpers';
import FHNWDateFormat from '../DateFormat/FHNWDateFormat';

export const occasionMessages = defineMessages({
  applyNow: {
    id: 'apply_now',
    defaultMessage: 'Apply Now',
  },
  labelOccNumber: {
    id: 'label_occ_number',
    defaultMessage: 'Occasion number',
  },
  labelOccDuration: {
    id: 'label_occ_duration',
    defaultMessage: 'Duration',
  },
  labelOccPlace: {
    id: 'label_occ_place',
    defaultMessage: 'Place',
  },
  labelFinalApplicationDate: {
    id: 'label_final_application_date',
    defaultMessage: 'Final application date',
  },
  labelApplicationStatus: {
    id: 'label_application_status',
    defaultMessage: 'Application status',
  },
  labelTotalPlaces: {
    id: 'label_total_places',
    defaultMessage: 'Total places',
  },
  labelFreePlaces: {
    id: 'label_free_places',
    defaultMessage: 'Free places',
  },
  labelCourseManagement: {
    id: 'label_course_management',
    defaultMessage: 'Course management',
  },
  labelAlternativeStart: {
    id: 'label_alternative_start',
    defaultMessage: 'Alternative start',
  },
  labelAdditionalInformation: {
    id: 'label_additional_information',
    defaultMessage: 'Additional information',
  },
  labelEventoId: {
    id: 'label_evento_id',
    defaultMessage: 'Evento ID',
  },
  hochschule: {
    id: 'Hochschule',
    defaultMessage: 'University',
  },
  fachbereich: {
    id: 'Faculty',
    defaultMessage: 'Faculty',
  },
  labelOrganisationEntities: {
    id: 'label_organisation_entities',
    defaultMessage: 'Organisation entities',
  },
  yes: {
    id: 'Yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'No',
    defaultMessage: 'No',
  },
});

interface VocabularyTerm {
  title: string;
  token: string;
}

interface TaxonomyTerm extends VocabularyTerm {}

export interface IOccasionContent extends PloneContent {
  alternative_start?: string;
  application_button_enabled?: boolean;
  application_button_label?: string;
  application_button_link?: string;
  application_status?: VocabularyTerm;
  duration?: string;
  place?: string;
  total_places?: string;
  free_places?: string;
  occ_number?: string;
  final_application_date?: string;
  course_management?: string;
  additional_information?: string;
  evento_id?: string;
  taxonomy_university?: TaxonomyTerm;
  taxonomy_fieldsofstudy?: TaxonomyTerm[];
  organisation_entities?: TaxonomyTerm[];
}

interface RenderFieldProps {
  label: React.ReactNode;
  value: string | boolean | number | null | undefined;
  renderHTML?: boolean;
  className?: string;
}

const RenderField: React.FC<RenderFieldProps> = ({
  label,
  value,
  renderHTML = false,
  className = '',
}) => {
  const intl = useIntl();
  if (value === null || value === undefined || value === '') {
    return null;
  }

  let displayValue = String(value);
  if (typeof value === 'boolean') {
    displayValue = value
      ? intl.formatMessage({ id: 'Yes', defaultMessage: 'Yes' })
      : intl.formatMessage({ id: 'No', defaultMessage: 'No' });
  }

  return (
    <div className={`occasion-field-row ${className}`}>
      <dt className="occasion-field-label">{label}</dt>
      {renderHTML ? (
        <dd
          className="occasion-field-value html-content"
          dangerouslySetInnerHTML={{ __html: displayValue }}
        />
      ) : (
        <dd className="occasion-field-value">{displayValue}</dd>
      )}
    </div>
  );
};

const OccasionView: React.FC<{ content: IOccasionContent }> = ({ content }) => {
  const intl = useIntl();

  const formatTaxonomy = (items: TaxonomyTerm[] | undefined) => {
    if (!items || items.length === 0) return null;
    return items.map((item) => item.title).join(', ');
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div id="occasion-view">
        <article id="page-document" className="occasion-view has-aside">
          <section className="content">
            <div className="blocks-group-wrapper transparent">
              <Title title={content.title} />
              {content.description && (
                <p className="documentDescription">{content.description}</p>
              )}
              {content.application_button_enabled &&
                content.application_button_link && (
                  <div className="application-button-wrapper">
                    <Link
                      className="btn btn-primary btn--md text-center ml-lg mr-lg mb-lg"
                      target="_blank"
                      linkComponent={UniversalLink}
                      href={
                        typeof content.application_button_link === 'object'
                          ? content.application_button_link['@id']
                          : content.application_button_link
                      }
                    >
                      {content.application_button_label ||
                        intl.formatMessage({
                          id: 'apply_now',
                          defaultMessage: 'Apply Now',
                        })}
                    </Link>
                  </div>
                )}

              <dl className="occasion-details">
                <RenderField
                  label={
                    <FormattedMessage
                      id="label_occ_number"
                      defaultMessage="Occasion number"
                    />
                  }
                  value={content.occ_number}
                />
                <RenderField
                  label={
                    <FormattedMessage
                      id="label_occ_duration"
                      defaultMessage="Duration"
                    />
                  }
                  value={content.duration}
                  renderHTML={true}
                />
                <RenderField
                  label={
                    <FormattedMessage
                      id="label_occ_place"
                      defaultMessage="Place"
                    />
                  }
                  value={content.place}
                />
                {content.final_application_date && (
                  <div className="occasion-field-row">
                    <dt className="occasion-field-label">
                      <FormattedMessage
                        id="label_final_application_date"
                        defaultMessage="Final application date"
                      />
                    </dt>
                    <dd className="occasion-field-value">
                      <FHNWDateFormat
                        start={new Date(content.final_application_date)}
                      />
                    </dd>
                  </div>
                )}

                <RenderField
                  label={
                    <FormattedMessage
                      id="label_application_status"
                      defaultMessage="Application status"
                    />
                  }
                  value={content.application_status?.title}
                />
                <RenderField
                  label={
                    <FormattedMessage
                      id="label_total_places"
                      defaultMessage="Total places"
                    />
                  }
                  value={content.total_places}
                />
                <RenderField
                  label={
                    <FormattedMessage
                      id="label_free_places"
                      defaultMessage="Free places"
                    />
                  }
                  value={content.free_places}
                />
                <RenderField
                  label={
                    <FormattedMessage
                      id="label_course_management"
                      defaultMessage="Course management"
                    />
                  }
                  value={content.course_management}
                />
                <RenderField
                  label={
                    <FormattedMessage
                      id="label_alternative_start"
                      defaultMessage="Alternative start"
                    />
                  }
                  value={content.alternative_start}
                  renderHTML={true}
                />
                <RenderField
                  label={
                    <FormattedMessage
                      id="label_additional_information"
                      defaultMessage="Additional information"
                    />
                  }
                  value={content.additional_information}
                  renderHTML={true}
                />
                <RenderField
                  label={
                    <FormattedMessage
                      id="label_evento_id"
                      defaultMessage="Evento ID"
                    />
                  }
                  value={content.evento_id}
                />
                {content.taxonomy_university && (
                  <RenderField
                    label={
                      <FormattedMessage
                        id="Hochschule"
                        defaultMessage="University"
                      />
                    }
                    value={formatTaxonomy([content.taxonomy_university])}
                  />
                )}
                <RenderField
                  label={
                    <FormattedMessage id="Faculty" defaultMessage="Faculty" />
                  }
                  value={formatTaxonomy(content.taxonomy_fieldsofstudy)}
                />
                <RenderField
                  label={
                    <FormattedMessage
                      id="label_organisation_entities"
                      defaultMessage="Organisation entities"
                    />
                  }
                  value={formatTaxonomy(content.organisation_entities)}
                />
              </dl>
            </div>
          </section>
          <aside className="sidebar-nav-right-wrapper">
            <SlotRenderer
              name="sidebar-nav-right"
              content={content as PloneContent}
            />
          </aside>
        </article>
      </div>
    </>
  );
};

export default OccasionView;
